// MARK://dhk8/admin-shell/pages/modules/FloorEditorPage
// Plano del salón — usa el componente compartido <FloorEditor> de
// `@dockethub/ui-restaurant`. Idéntico al editor del POS (mismo componente, mismas
// features). Polling cada 30s para que cambios desde el POS aparezcan acá.
import { useEffect, useRef, useState } from 'react'
import { FloorEditor, type FloorEditorAddTableInput, type TableDto } from '@dockethub/ui-restaurant'
import * as floor from '../../lib/clients/floor.js'
import type { FloorLayout } from '../../lib/clients/floor.js'
import { useAsync, asyncState } from '../../lib/use-async.js'

const SECTION_COLORS = ['var(--hig-green-600)', 'var(--pos-info)', 'var(--pos-warning)', 'var(--pos-tint-strong)', 'var(--hig-green-400)']
const POLL_MS = 30_000

function toTableDto(t: FloorLayout['tables'][number]): TableDto {
  return {
    id: t.id, code: t.code, capacity: t.capacity, posX: t.posX, posY: t.posY,
    widthPx: t.widthPx, heightPx: t.heightPx,
    shape: t.shape === 'ROUND' ? 'ROUND' : 'RECT',
    status: 'AVAILABLE', sectionId: t.sectionId,
  }
}

export default function FloorEditorPage() {
  const { data, loading, error, reload, setData } = useAsync<FloorLayout>((s) => floor.getLayout(s))
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const moved = useRef<Set<string>>(new Set())

  // Polling — refresh every 30s + on window focus.
  useEffect(() => {
    const iv = setInterval(() => { if (!dirty && !saving) reload() }, POLL_MS)
    const onFocus = () => { if (!dirty && !saving) reload() }
    window.addEventListener('focus', onFocus)
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus) }
  }, [reload, dirty, saving])

  useEffect(() => { setDirty(false); moved.current.clear() }, [loading])

  function handleTablesChange(next: TableDto[]) {
    if (!data) return
    const byId = new Map(next.map((t) => [t.id, t]))
    const merged = data.tables.map((orig) => {
      const upd = byId.get(orig.id); if (!upd) return orig
      if (upd.posX !== orig.posX || upd.posY !== orig.posY) moved.current.add(orig.id)
      return { ...orig, posX: upd.posX, posY: upd.posY }
    })
    setData({ ...data, tables: merged })
    setDirty(true)
  }

  async function handleSave() {
    if (!data) return
    setSaving(true)
    try {
      for (const tbl of data.tables) {
        if (moved.current.has(tbl.id)) await floor.saveTable(tbl)
      }
      moved.current.clear()
      setDirty(false)
    } finally { setSaving(false) }
  }

  async function handleAddTable(input: FloorEditorAddTableInput) {
    if (!data) return
    await floor.createTable({
      code: input.code, capacity: input.capacity,
      posX: 40, posY: 40,
      widthPx: input.shape === 'ROUND' ? 80 : 120,
      heightPx: input.shape === 'ROUND' ? 80 : 90,
      shape: input.shape,
      sectionId: input.sectionId,
    })
    reload()
  }

  async function handleDeleteTable(id: string) { await floor.deleteTable(id); reload() }

  async function handleUpdateTable(id: string, updates: Partial<TableDto> & { sectionId?: string }) {
    if (!data) return
    const orig = data.tables.find((t) => t.id === id); if (!orig) return
    const next = { ...orig, ...updates, shape: (updates.shape ?? orig.shape) as 'RECT' | 'ROUND' }
    setData({ ...data, tables: data.tables.map((t) => t.id === id ? next : t) })
    await floor.saveTable(next)
  }

  async function handleAddSection(name: string) { await floor.createSection(name); reload() }
  async function handleDeleteSection(id: string) { await floor.deleteSection(id); reload() }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Plano</h1><p className="adm-page__sub">Mismo editor que ve la mesera en el POS · cambios sincronizan cada 30s.</p></div>
      </div>
      {asyncState(loading, error, reload)}
      {data && (
        <FloorEditor
          tables={data.tables.map(toTableDto)}
          sections={data.sections}
          floorWidthPx={data.widthPx}
          floorHeightPx={data.heightPx}
          editing
          sectionColorByIndex={SECTION_COLORS}
          tableSectionId={(id) => data.tables.find((t) => t.id === id)?.sectionId}
          onTablesChange={handleTablesChange}
          onSave={handleSave}
          saving={saving}
          dirty={dirty}
          onAddTable={handleAddTable}
          onDeleteTable={handleDeleteTable}
          onUpdateTable={handleUpdateTable}
          onAddSection={handleAddSection}
          onDeleteSection={handleDeleteSection}
          onRefresh={reload}
          title="Plano"
        />
      )}
    </div>
  )
}
